import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, FLAW, ANSWERS, CHAT, QUIZ, CONTROL, HONEST, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK: shipped the quality layer, but can't prove it yet */
const HOOK_CHECKS = ["grounding", "citations", "completeness", "order"];
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="build-in-public · shipped 2026-07-19" color={COLORS.cyan} />
          {/* four check-chips light up = "answers get checked before you see them" */}
          <div style={{ display: "flex", gap: 14, marginBottom: 34 }}>
            {HOOK_CHECKS.map((label, i) => {
              const on = interpolate(frame, [20 + i * 12, 40 + i * 12], [0.12, 1], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              });
              return (
                <div
                  key={label}
                  style={{
                    opacity: on,
                    fontFamily: FONTS.mono,
                    fontSize: 20,
                    color: COLORS.green,
                    border: `1px solid ${COLORS.green}66`,
                    background: COLORS.panel,
                    borderRadius: 8,
                    padding: "8px 16px",
                    boxShadow: `0 0 26px -14px ${COLORS.green}`,
                  }}
                >
                  ✓ {label}
                </div>
              );
            })}
          </div>
          <Big size={92}>
            The quality layer{" "}
            <span style={{ color: COLORS.green }}>shipped</span>.
            <br />
            Proving it is{" "}
            <span style={{ color: COLORS.cyan }}>the next episode</span>.
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — THE FLAW: fluent, cited, incomplete */
export const Scene02Flaw: React.FC = () => {
  const frame = useCurrentFrame();
  const stamp = interpolate(frame, [220, 250], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.flaw.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="before · what an answer used to do" color={COLORS.red} />
          <Panel accent="red" title="generated answer" width={1120}>
            <div
              style={{
                fontFamily: FONTS.body,
                fontSize: 32,
                lineHeight: 1.55,
                color: COLORS.text,
                padding: "8px 6px",
              }}
            >
              “You should{" "}
              <span style={{ color: COLORS.amber }}>build a dashboard</span> for this,
              as covered in{" "}
              <span style={{ color: COLORS.cyan }}>[Source 7]</span>.”
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 20,
                  color: COLORS.dim,
                  marginTop: 18,
                  lineHeight: 1.7,
                }}
              >
                ↳ the dashboard <span style={{ color: COLORS.red }}>already ships</span> in the product
                <br />
                ↳ <span style={{ color: COLORS.red }}>[Source 7]</span> was never in the retrieved set
                <br />
                ↳ two facts it <span style={{ color: COLORS.red }}>retrieved</span> never made the answer
              </div>
            </div>
          </Panel>
          <div
            style={{
              marginTop: 28,
              opacity: stamp,
              transform: `scale(${0.9 + stamp * 0.1})`,
              fontFamily: FONTS.mono,
              fontSize: 28,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: COLORS.red,
              border: `2px solid ${COLORS.red}`,
              padding: "8px 22px",
              borderRadius: 8,
            }}
          >
            fluent · cited · incomplete
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={FLAW} accent="red" />
    </AbsoluteFill>
  );
};

/* 03 — FOUR ANSWER-CHECKING LAYERS (all default-ON 2026-07-19) */
const CHECKS: PipeNode[] = [
  { label: "completeness", value: "SPLICE", sub: "add dropped facts", accent: "green" },
  { label: "current-state", value: "RECONCILE", sub: "you already built X", accent: "cyan" },
  { label: "footer", value: "CITED-ONLY", sub: "prune unused sources", accent: "violet" },
  { label: "auditor", value: "ENTAILMENT", sub: "background · sampled", accent: "amber" },
];
export const Scene03Answers: React.FC = () => {
  const frame = useCurrentFrame();
  const show = interpolate(frame, [320, 350], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.answers.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
        <Kicker text="checked before you see the reply" color={COLORS.green} />
        <div style={{ width: 1740 }}>
          <PipelineGraph nodes={CHECKS} startFrame={8} stepFrames={26} nodeWidth={250} />
        </div>
        <div
          style={{
            display: "flex",
            gap: 28,
            marginTop: 48,
            opacity: show,
            alignItems: "baseline",
          }}
        >
          <MetricCounter value={6} startFrame={325} durationFrames={45} accent="green" fontSize={120} />
          <Callout label="completeness critic · test session" accent="green" width={480}>
            spliced <b style={{ color: COLORS.text }}>6 supported facts</b> the first draft dropped — the original answer, not a rewrite
          </Callout>
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 22,
            color: COLORS.dim,
            marginTop: 26,
            opacity: show,
          }}
        >
          all four <span style={{ color: COLORS.green }}>default-ON</span> · 2026-07-19 · switchable per tenant
        </div>
      </AbsoluteFill>
      <Caption lines={ANSWERS} accent="green" />
    </AbsoluteFill>
  );
};

/* 04 — RELIABLE CHAT MECHANICS: the transcript behaves */
export const Scene04Chat: React.FC = () => {
  const frame = useCurrentFrame();
  const list = interpolate(frame, [150, 180], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.chat.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
        <Kicker text="the transcript behaves" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <MetricCounter value={0} startFrame={12} durationFrames={40} accent="green" fontSize={168} />
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 20,
                color: COLORS.dim,
                marginTop: 6,
                textAlign: "center",
                maxWidth: 380,
              }}
            >
              answer-before-question<br />ordering violations · <span style={{ color: COLORS.green }}>verified live</span>
            </div>
          </div>
          <div style={{ opacity: list }}>
            <Panel accent="cyan" title="what got fixed" width={720}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 24, lineHeight: 1.95, color: COLORS.text }}>
                <div>
                  <span style={{ color: COLORS.green }}>order holds after refresh</span>{" "}
                  <span style={{ color: COLORS.dim }}>· deterministic order</span>
                </div>
                <div>
                  <span style={{ color: COLORS.green }}>delete a turn = whole turn</span>{" "}
                  <span style={{ color: COLORS.dim }}>· no orphaned rows</span>
                </div>
                <div>
                  <span style={{ color: COLORS.green }}>cost badge always shows</span>{" "}
                  <span style={{ color: COLORS.dim }}>· ~$0.0042 est.</span>
                </div>
                <div>
                  <span style={{ color: COLORS.green }}>themed diagrams render</span>{" "}
                  <span style={{ color: COLORS.dim }}>· mermaid fix</span>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={CHAT} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 05 — QUIZZES & LEARNING PATHS STAY GROUNDED (before → after) */
export const Scene05Quiz: React.FC = () => {
  const frame = useCurrentFrame();
  const after = interpolate(frame, [150, 185], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.quiz.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="quizzes & learning paths · on your repo" color={COLORS.lime} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent="red" title="before · leaked & broken" width={600}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.85, color: COLORS.text }}>
              <div>taught <span style={{ color: COLORS.red }}>“AZ-040”</span></div>
              <div style={{ color: COLORS.dim, fontSize: 19 }}>a Microsoft course, not your code</div>
              <div style={{ color: COLORS.red, marginTop: 8 }}>true/false had no buttons</div>
              <div style={{ color: COLORS.red }}>a dead question blocked the score</div>
            </div>
          </Panel>
          <div style={{ opacity: after, alignSelf: "center" }}>
            <Panel accent="lime" title="after · grounded" width={600}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.85, color: COLORS.text }}>
                <div><span style={{ color: COLORS.lime }}>off-topic catalog quarantined</span></div>
                <div style={{ color: COLORS.dim, fontSize: 19 }}>title + description must co-occur</div>
                <div style={{ color: COLORS.lime, marginTop: 8 }}>True / False buttons grade right</div>
                <div style={{ color: COLORS.lime }}>every question answerable · anchored</div>
              </div>
            </Panel>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={QUIZ} accent="lime" />
    </AbsoluteFill>
  );
};

/* 06 — OPERATOR CONTROL: Feature Switches (no black box) */
const SWITCHES = [
  { label: "Completeness critic", state: "ON" },
  { label: "Current-state reconciler", state: "ON" },
  { label: "Citation auditing", state: "SAMPLED" },
];
const SwitchRow: React.FC<{ label: string; state: string; at: number }> = ({ label, state, at }) => {
  const frame = useCurrentFrame();
  const p = interpolate(frame, [at, at + 18], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 4px",
        borderBottom: `1px solid ${COLORS.line}`,
      }}
    >
      <span style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.text }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ fontFamily: FONTS.mono, fontSize: 18, letterSpacing: "0.1em", color: COLORS.green }}>
          {state}
        </span>
        {/* pill toggle: track fills green + knob slides to ON */}
        <div
          style={{
            width: 64,
            height: 32,
            borderRadius: 20,
            background: COLORS.line,
            border: `1px solid ${COLORS.green}66`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: COLORS.green,
              opacity: p * 0.55,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 3,
              left: 3 + p * 32,
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: COLORS.text,
              boxShadow: `0 0 14px -4px ${COLORS.green}`,
            }}
          />
        </div>
      </div>
    </div>
  );
};
export const Scene06Control: React.FC = () => {
  const frame = useCurrentFrame();
  const lesson = interpolate(frame, [220, 250], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.control.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="nothing runs as a black box" color={COLORS.amber} />
        <Panel accent="amber" title="admin → feature switches" width={880}>
          <div style={{ padding: "2px 6px" }}>
            {SWITCHES.map((s, i) => (
              <SwitchRow key={s.label} label={s.label} state={s.state} at={20 + i * 22} />
            ))}
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 18,
                color: COLORS.dim,
                marginTop: 12,
              }}
            >
              saved instantly · picked up in ~60s · no redeploy · per tenant
            </div>
          </div>
        </Panel>
        <div style={{ marginTop: 30, opacity: lesson, maxWidth: 1080 }}>
          <Callout label="↳ the lesson that made the toggles necessary" accent="amber" width={1080}>
            a runtime flag needs all four layers —{" "}
            <b style={{ color: COLORS.text }}>type · storage · API · UI</b> — or it's silently unreachable
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={CONTROL} accent="amber" />
    </AbsoluteFill>
  );
};

/* 07 — SHIPPED vs STILL-BEING-MEASURED (the honest beat) */
export const Scene07Honest: React.FC = () => {
  const frame = useCurrentFrame();
  const c2 = interpolate(frame, [170, 200], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.honest.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the honest part · definition of shipped" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent="green" title="shipped · observable" width={600}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.8, color: COLORS.text }}>
              <div>4 answer checks · default-ON</div>
              <div>order · delete · cost · diagrams</div>
              <div>grounded quizzes · real toggles</div>
              <div style={{ color: COLORS.green, marginTop: 6 }}>reachable end-to-end</div>
            </div>
          </Panel>
          <div style={{ opacity: c2, alignSelf: "center" }}>
            <Panel accent="amber" title="not proven yet" width={600}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.8, color: COLORS.text }}>
                <div>P1 eval harness = half-built</div>
                <div style={{ color: COLORS.dim }}>can't quantify the gain yet</div>
                <div style={{ color: COLORS.amber, marginTop: 6 }}>P2 / P3 / P5 deferred</div>
                <div style={{ color: COLORS.dim, fontSize: 19 }}>18-agent realism council said wait</div>
              </div>
            </Panel>
          </div>
        </div>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 26,
            color: COLORS.dim,
            marginTop: 34,
            textAlign: "center",
          }}
        >
          shipped <span style={{ color: COLORS.text }}>≠</span> numerically proven — the harness comes next
        </div>
      </AbsoluteFill>
      <Caption lines={HONEST} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={78}>
          Shipped — not a{" "}
          <span style={{ color: COLORS.amber }}>victory lap</span>.
          <br />
          <span style={{ color: COLORS.cyan }}>Proving it comes next.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · build-in-public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
